import React, { useEffect, useState } from 'react';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { Sidebar } from './components/layout/Sidebar';
import { CookieBanner } from './components/layout/CookieBanner';
import { PAGE_ROUTE } from './lib/routes';
import { getSession, clearSession, Session } from './lib/utils';
// Public pages
import { LandingPage } from './pages/LandingPage';
import { TeasingPage } from './pages/TeasingPage';
import { Marketplace } from './pages/Marketplace';
import { TenantProfile } from './pages/TenantProfile';
import { TenantAnunt } from './pages/TenantAnunt';
import { LandlordProfile } from './pages/LandlordProfile';
import { Contact } from './pages/Contact';
import { Termeni } from './pages/Termeni';
import { Confidentialitate } from './pages/Confidentialitate';
import { Changelog } from './pages/Changelog';
// Auth pages
import { Register } from './pages/Register';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { ResetPasswordToken } from './pages/ResetPasswordToken';
import { Onboarding } from './pages/Onboarding';
// Dashboard pages
import { DashboardProfil } from './pages/DashboardProfil';
import { DashboardAnunt } from './pages/DashboardAnunt';
import { DashboardAplicari } from './pages/DashboardAplicari';
import { DashboardAplicariArchive } from './pages/DashboardAplicariArchive';
import { DashboardProfileSalvate } from './pages/DashboardProfileSalvate';
import { DashboardSetari } from './pages/DashboardSetari';
import { ReviewSubmission } from './pages/ReviewSubmission';
import { PropertyEditor } from './pages/PropertyEditor';
import { DemoPersonasSwitcher } from './components/dev/DemoPersonasSwitcher';
import { Pricing } from './pages/Pricing';
import { initBetaCredits, getCreditsState } from './lib/credits';
import { IS_BETA, BETA_FREE_CREDITS } from './lib/constants';
import { useScreenInit } from './useScreenInit';
const DASHBOARD_PAGES = [PAGE_ROUTE.DASHBOARD, PAGE_ROUTE.DASHBOARD_PROFIL, PAGE_ROUTE.DASHBOARD_ANUNT, PAGE_ROUTE.DASHBOARD_APLICARI, PAGE_ROUTE.DASHBOARD_APLICARI_ARCHIVE, PAGE_ROUTE.DASHBOARD_PROFILE_SALVATE, PAGE_ROUTE.DASHBOARD_SETARI];
const AUTH_PAGES = [PAGE_ROUTE.LOGIN, PAGE_ROUTE.REGISTER, PAGE_ROUTE.RESET_PASSWORD, PAGE_ROUTE.RESET_PASSWORD_TOKEN];
const FULLSCREEN_PAGES = [PAGE_ROUTE.HOME, PAGE_ROUTE.ONBOARDING, PAGE_ROUTE.REVIEW_SUBMISSION, PAGE_ROUTE.PROPERTY_EDITOR, PAGE_ROUTE.TEASING];
export function App() {
  const screenInit = useScreenInit();
  const [currentPage, setCurrentPage] = useState<string>(screenInit?.currentPage || PAGE_ROUTE.HOME);
  const [pageContext, setPageContext] = useState<any>(null);
  const [session, setSessionState] = useState<Session | null>(() => getSession());
  // Beta: grant 50 free credits the first time a supply user is detected.
  useEffect(() => {
    if (!IS_BETA) return;
    if (session?.role !== 'supply') return;
    const state = getCreditsState();
    if (!state.initialized) {
      initBetaCredits(BETA_FREE_CREDITS);
    }
  }, [session?.role]);
  const navigate = (page: string, context?: any) => {
    // DASHBOARD routes to DASHBOARD_PROFIL by default
    if (page === PAGE_ROUTE.DASHBOARD) {
      setCurrentPage(PAGE_ROUTE.DASHBOARD_PROFIL);
    } else {
      setCurrentPage(page);
    }
    setPageContext(context || null);
    // Refresh session from storage in case Register/Login/Onboarding just set it
    setSessionState(getSession());
    window.scrollTo(0, 0);
  };
  const handleSignOut = () => {
    clearSession();
    setSessionState(null);
  };
  const headerSession = session ? {
    user: {
      name: session.name,
      email: session.email
    },
    role: session.role
  } : null;
  const isDashboard = DASHBOARD_PAGES.includes(currentPage);
  const isAuth = AUTH_PAGES.includes(currentPage);
  const isFullscreen = FULLSCREEN_PAGES.includes(currentPage);
  const renderPage = () => {
    switch (currentPage) {
      // Public pages
      case PAGE_ROUTE.HOME:
        return <TeasingPage navigate={navigate} data-id="element-0" />;
      case PAGE_ROUTE.TEASING:
        return <TeasingPage navigate={navigate} data-id="element-1" />;
      case PAGE_ROUTE.MARKETPLACE:
        return <Marketplace navigate={navigate} data-id="element-2" />;
      case PAGE_ROUTE.TENANT_PROFILE:
        return <TenantProfile navigate={navigate} context={pageContext} data-id="element-3" />;
      case PAGE_ROUTE.TENANT_ANUNT:
        return <TenantAnunt navigate={navigate} context={pageContext} data-id="element-4" />;
      case PAGE_ROUTE.LANDLORD_PROFILE:
        return <LandlordProfile navigate={navigate} context={pageContext} data-id="element-5" />;
      case PAGE_ROUTE.CONTACT:
        return <Contact navigate={navigate} data-id="element-6" />;
      case PAGE_ROUTE.TERMENI:
        return <Termeni navigate={navigate} data-id="element-7" />;
      case PAGE_ROUTE.CONFIDENTIALITATE:
        return <Confidentialitate navigate={navigate} data-id="element-8" />;
      case PAGE_ROUTE.CHANGELOG:
        return <Changelog navigate={navigate} data-id="element-9" />;
      case PAGE_ROUTE.PRICING:
        return <Pricing navigate={navigate} data-id="element-10" />;
      // Auth pages
      case PAGE_ROUTE.REGISTER:
        return <Register navigate={navigate} context={pageContext} data-id="element-11" />;
      case PAGE_ROUTE.LOGIN:
        return <Login navigate={navigate} context={pageContext} data-id="element-12" />;
      case PAGE_ROUTE.RESET_PASSWORD:
        return <ResetPassword navigate={navigate} data-id="element-13" />;
      case PAGE_ROUTE.RESET_PASSWORD_TOKEN:
        return <ResetPasswordToken navigate={navigate} context={pageContext} data-id="element-14" />;
      case PAGE_ROUTE.ONBOARDING:
        return <Onboarding navigate={navigate} data-id="element-15" />;
      case PAGE_ROUTE.REVIEW_SUBMISSION:
        return <ReviewSubmission navigate={navigate} context={pageContext} data-id="element-16" />;
      // Dashboard pages
      case PAGE_ROUTE.DASHBOARD_PROFIL:
        return <DashboardProfil navigate={navigate} data-id="element-17" />;
      case PAGE_ROUTE.DASHBOARD_ANUNT:
        return <DashboardAnunt navigate={navigate} data-id="element-18" />;
      case PAGE_ROUTE.DASHBOARD_APLICARI:
        return <DashboardAplicari navigate={navigate} data-id="element-19" />;
      case PAGE_ROUTE.DASHBOARD_APLICARI_ARCHIVE:
        return <DashboardAplicariArchive navigate={navigate} data-id="element-20" />;
      case PAGE_ROUTE.DASHBOARD_PROFILE_SALVATE:
        return <DashboardProfileSalvate navigate={navigate} data-id="element-21" />;
      case PAGE_ROUTE.DASHBOARD_SETARI:
        return <DashboardSetari navigate={navigate} data-id="element-22" />;
      case PAGE_ROUTE.PROPERTY_EDITOR:
        return <PropertyEditor navigate={navigate} context={pageContext} data-id="element-23" />;
      default:
        return <TeasingPage navigate={navigate} data-id="element-24" />;
    }
  };
  // Fullscreen layout: page renders by itself (no header/footer/sidebar)
  if (isFullscreen) {
    return <div className="min-h-screen bg-dark-1 text-foreground font-sans" data-id="element-25">
        {renderPage()}
      </div>;
  }
  // Dashboard layout: Header + Sidebar + content (no Footer)
  if (isDashboard) {
    return <div className="min-h-screen bg-dark-1 text-foreground font-sans" data-id="element-26">
        <Header currentPage={currentPage} navigate={navigate} session={headerSession} onSignOut={handleSignOut} data-id="element-27" />
        <Sidebar currentPage={currentPage} navigate={navigate} role={session?.role} data-id="element-28" />
        <main className="lg:pl-64 min-h-screen bg-dark-1 pb-20 lg:pb-0" data-id="element-29">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-30">
            {renderPage()}
          </div>
        </main>
        <CookieBanner data-id="element-31" />
        <DemoPersonasSwitcher navigate={navigate} data-id="element-32" />
      </div>;
  }
  // Auth layout: Header + centered content (no Footer)
  if (isAuth) {
    return <div className="min-h-screen bg-dark-1 text-foreground font-sans flex flex-col" data-id="element-33">
        <Header currentPage={currentPage} navigate={navigate} session={headerSession} onSignOut={handleSignOut} data-id="element-34" />
        <main className="flex-1 flex items-center justify-center" data-id="element-35">
          {renderPage()}
        </main>
        <CookieBanner data-id="element-36" />
        <DemoPersonasSwitcher navigate={navigate} data-id="element-37" />
      </div>;
  }
  // Public layout: Header + content + Footer
  return <div className="min-h-screen bg-dark-1 text-foreground font-sans flex flex-col" data-id="element-38">
      <Header currentPage={currentPage} navigate={navigate} session={headerSession} onSignOut={handleSignOut} data-id="element-39" />
      <main className="flex-1 flex flex-col" data-id="element-40">{renderPage()}</main>
      <Footer navigate={navigate} data-id="element-41" />
      <CookieBanner data-id="element-42" />
      <DemoPersonasSwitcher navigate={navigate} data-id="element-43" />
    </div>;
}